import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { AuthAdapter, AuthResult, AuthUser } from '../types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined
const configured = Boolean(supabaseUrl && supabaseKey)
const client: SupabaseClient | null = configured ? createClient(supabaseUrl!, supabaseKey!, { auth: { flowType: 'pkce', persistSession: true, detectSessionInUrl: true } }) : null

const mapUser = (user: { id: string; email?: string; user_metadata?: Record<string, unknown> } | null): AuthUser | null => user ? {
  id: user.id,
  email: user.email || '',
  name: String(user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || '오마이트립 회원'),
} : null

const unavailable = '로그인 기능을 준비하고 있어요. 지금은 로그인 없이 예약할 수 있어요.'

export const authAdapter: AuthAdapter = {
  configured,
  async getUser() {
    if (!client) return null
    const { data } = await client.auth.getUser()
    return mapUser(data.user)
  },
  async signInWithPassword(email, password): Promise<AuthResult> {
    if (!client) return { user: null, error: unavailable }
    const { data, error } = await client.auth.signInWithPassword({ email, password })
    return { user: mapUser(data.user), error: error?.message || null }
  },
  async signUp(email, password, name): Promise<AuthResult> {
    if (!client) return { user: null, error: unavailable }
    const { data, error } = await client.auth.signUp({ email, password, options: { data: { full_name: name }, emailRedirectTo: `${location.origin}/auth/callback` } })
    return { user: mapUser(data.user), error: error?.message || null }
  },
  async signInWithOAuth(provider, returnTo = '/') {
    if (!client) return { error: unavailable }
    sessionStorage.setItem('ohmytrip:auth:returnTo', returnTo)
    const { error } = await client.auth.signInWithOAuth({ provider, options: { redirectTo: `${location.origin}/auth/callback` } })
    return { error: error?.message || null }
  },
  async resetPassword(email) {
    if (!client) return { error: unavailable }
    const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo: `${location.origin}/auth/callback?mode=recovery` })
    return { error: error?.message || null }
  },
  async exchangeCode(code): Promise<AuthResult> {
    if (!client) return { user: null, error: unavailable }
    const { data, error } = await client.auth.exchangeCodeForSession(code)
    return { user: mapUser(data.user), error: error?.message || null }
  },
  async signOut() { await client?.auth.signOut() },
  onAuthStateChange(callback) {
    if (!client) return () => undefined
    const { data } = client.auth.onAuthStateChange((_event, session) => callback(mapUser(session?.user || null)))
    return () => data.subscription.unsubscribe()
  },
}
